import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  CircularProgress, Alert, Button, Stack, Divider, Chip, Avatar,
  IconButton, Tooltip, Breadcrumbs, Badge, LinearProgress, Fade, Zoom,
  Snackbar
} from '@mui/material';
import {
  ArrowBack, Share, Visibility, ThumbUp, ThumbDown,
  CalendarToday, Speed, Psychology, Category, Analytics,
  PhotoCamera, Info, Timeline, TrendingUp, Public, EmojiEvents,
  AutoAwesome, Science, Recycling
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getClassificationDetails } from '../api/classify'; // Use existing API client
import TipDisplay from '../components/edutips/TipDisplay';

const ClassificationDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [classification, setClassification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add snackbar state for share feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchClassificationDetails();
  }, [id]);

  const fetchClassificationDetails = async () => {
    try {
      setLoading(true);
      // Use the proper API client instead of hardcoded fetch
      const response = await getClassificationDetails(id);
      setClassification(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch classification details:', err);
      setError('Classification not found or you don\'t have permission to view it.');
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const getCategoryColor = (category) => {
    const colors = {
      'Recyclable': '#43a047',
      'Organic': '#66bb6a',
      'Hazardous': '#e53935',
      'General': '#546e7a',
      'Electronic': '#2196f3'
    };
    return colors[category] || '#546e7a';
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      'Recyclable': 'linear-gradient(135deg, #43a047, #66bb6a)',
      'Organic': 'linear-gradient(135deg, #66bb6a, #81c784)',
      'Hazardous': 'linear-gradient(135deg, #e53935, #ef5350)',
      'General': 'linear-gradient(135deg, #546e7a, #607d8b)',
      'Electronic': 'linear-gradient(135deg, #2196f3, #42a5f5)'
    };
    return gradients[category] || 'linear-gradient(135deg, #546e7a, #607d8b)';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Recyclable': '♻️',
      'Organic': '🌱',
      'Hazardous': '⚠️',
      'General': '🗑️',
      'Electronic': '🔌'
    };
    return icons[category] || '📦';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return '#43a047';
    if (confidence >= 70) return '#ff9800';
    return '#f44336';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 90) return 'Very High';
    if (confidence >= 70) return 'Good';
    if (confidence >= 50) return 'Moderate';
    return 'Low';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShare = async () => {
    try {
      // FIXED: Use shareToken instead of classification.id like in HistoryPage
      const shareUrl = `${window.location.origin}/shared-classification/${classification.shareToken}`;
      const shareText = `Check out my waste classification result on Sortify! 🌱\n\nCategory: ${classification.category?.name || 'Unknown'}\nConfidence: ${Math.round(classification.resultConfidence || 0)}%\n\n${shareUrl}`;

      if (navigator.share) {
        await navigator.share({
          title: 'My Waste Classification - Sortify',
          text: shareText,
          url: shareUrl
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareText);
        setSnackbarMessage('🔗 Share link copied to clipboard! Share your eco achievement!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setSnackbarMessage('❌ Failed to share. Please try again.');
      setSnackbarOpen(true);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} sx={{ color: '#43a047' }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ mb: 4, borderRadius: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/history')}>
              Back to History
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!classification) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h5" color="text.secondary" sx={{ textAlign: 'center' }}>
          Classification not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: `
        radial-gradient(circle at 20% 80%, rgba(67, 160, 71, 0.12) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 196, 0, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(0, 184, 148, 0.08) 0%, transparent 50%),
        linear-gradient(135deg, #f8fdf8 0%, #e8f5e8 50%, #f0f8f0 100%)
      `,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2343a047' fill-opacity='0.02'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
        `,
        opacity: 0.3,
        animation: 'float 20s ease-in-out infinite',
        pointerEvents: 'none',
      },
      '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
        '50%': { transform: 'translateY(-10px) rotate(2deg)' },
      }
    }}>
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Enhanced Breadcrumbs */}
        <Fade in timeout={800}>
          <Paper elevation={0} sx={{
            p: 2,
            mb: 4,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(67, 160, 71, 0.1)'
          }}>
            <Breadcrumbs separator="›" sx={{
              '& .MuiBreadcrumbs-separator': {
                color: '#43a047',
                fontWeight: 800
              }
            }}>
              <Link to="/" style={{
                textDecoration: 'none',
                color: '#43a047',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                🏠 Home
              </Link>
              <Link to="/history" style={{
                textDecoration: 'none',
                color: '#43a047',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                📊 History
              </Link>
              <Typography color="text.primary" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                🎯 Waste Classification Details
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Fade>

        {/* Enhanced Header */}
        <Fade in timeout={1000}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 6,
            p: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,255,248,0.85))',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 12px 40px rgba(67, 160, 71, 0.15)',
            border: '2px solid rgba(67, 160, 71, 0.1)'
          }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/history')}
              size="large"
              sx={{
                color: '#43a047',
                fontWeight: 700,
                px: 3,
                py: 1.5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.1), rgba(102, 187, 106, 0.05))',
                border: '2px solid rgba(67, 160, 71, 0.2)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.15), rgba(102, 187, 106, 0.1))',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(67, 160, 71, 0.25)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Back to History
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{
                color: '#43a047',
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <AutoAwesome sx={{ color: '#ffc400' }} />
                Share Your Impact
              </Typography>
              <Tooltip title="Share this classification publicly and inspire others to sort waste responsibly!">
                <IconButton
                  onClick={handleShare}
                  size="large"
                  sx={{
                    background: 'linear-gradient(135deg, #ff9800, #f57c00)',
                    color: 'white',
                    boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #f57c00, #ef6c00)',
                      transform: 'translateY(-3px) rotate(5deg)',
                      boxShadow: '0 12px 35px rgba(255, 152, 0, 0.4)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          {/* Enhanced Image Section */}
          <Grid item xs={12} md={6}>
            <Zoom in timeout={1200}>
              <Paper elevation={20} sx={{
                borderRadius: 5,
                overflow: 'hidden',
                border: `6px solid ${getCategoryColor(classification.category?.name)}`,
                background: `linear-gradient(135deg, ${getCategoryColor(classification.category?.name)}20, transparent)`,
                position: 'relative',
                transform: 'perspective(1000px) rotateY(-5deg)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'perspective(1000px) rotateY(0deg) translateY(-10px)',
                  boxShadow: `0 25px 60px ${alpha(getCategoryColor(classification.category?.name), 0.4)}`
                }
              }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={classification.imageUrl || classification.imagePath}
                    alt="Waste Classification Result"
                    onError={(e) => {
                      console.error('Image failed to load. Tried imageUrl:', classification.imageUrl, 'imagePath:', classification.imagePath);
                      e.target.style.display = 'none';
                    }}
                    sx={{
                      width: '100%',
                      height: '450px',
                      objectFit: 'cover',
                      background: getCategoryGradient(classification.category?.name),
                      filter: 'brightness(1.1) contrast(1.05) saturate(1.1)'
                    }}
                  />

                  {/* Smart Classification Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(getCategoryColor(classification.category?.name), 0.15)}, transparent 70%)`,
                    pointerEvents: 'none'
                  }} />

                  {/* Enhanced Category & Confidence Badges */}
                  <Box sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    right: 20,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <Chip
                      icon={<span style={{ fontSize: '2rem' }}>{getCategoryIcon(classification.category?.name)}</span>}
                      label={classification.category?.name || 'Unknown'}
                      size="large"
                      sx={{
                        background: `linear-gradient(135deg, ${getCategoryColor(classification.category?.name)}, ${alpha(getCategoryColor(classification.category?.name), 0.8)})`,
                        color: 'white',
                        fontWeight: 900,
                        fontSize: '1.3rem',
                        px: 3,
                        py: 2,
                        height: 'auto',
                        backdropFilter: 'blur(15px)',
                        boxShadow: `0 12px 40px ${alpha(getCategoryColor(classification.category?.name), 0.5)}`,
                        border: '3px solid white',
                        transform: 'translateY(0px)',
                        animation: 'bounce 2s infinite',
                        '& .MuiChip-icon': {
                          color: 'white',
                          fontSize: '2rem'
                        },
                        '@keyframes bounce': {
                          '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0px)' },
                          '40%': { transform: 'translateY(-8px)' },
                          '60%': { transform: 'translateY(-4px)' }
                        }
                      }}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip
                        icon={<EmojiEvents sx={{ color: 'white !important' }} />}
                        label={`${classification.resultConfidence?.toFixed(1)}%`}
                        size="large"
                        sx={{
                          background: `linear-gradient(135deg, ${getConfidenceColor(classification.resultConfidence)}, ${alpha(getConfidenceColor(classification.resultConfidence), 0.8)})`,
                          color: 'white',
                          fontWeight: 900,
                          fontSize: '1.2rem',
                          px: 2,
                          py: 1.5,
                          height: 'auto',
                          backdropFilter: 'blur(15px)',
                          boxShadow: `0 10px 30px ${alpha(getConfidenceColor(classification.resultConfidence), 0.4)}`,
                          border: '3px solid white',
                          '& .MuiChip-icon': {
                            color: 'white'
                          }
                        }}
                      />
                      <Chip
                        label={getConfidenceLabel(classification.resultConfidence)}
                        size="small"
                        sx={{
                          background: 'rgba(255,255,255,0.95)',
                          color: getConfidenceColor(classification.resultConfidence),
                          fontWeight: 700,
                          backdropFilter: 'blur(10px)',
                          border: `2px solid ${getConfidenceColor(classification.resultConfidence)}`
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Smart Sorter Badge */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: 'linear-gradient(135deg, #4caf50, #43a047)',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 800,
                    boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
                    border: '2px solid white',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <Recycling sx={{ fontSize: '1.2rem' }} />
                    <Typography variant="body2" fontWeight={800}>
                      AI Sorted
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Zoom>
          </Grid>

          {/* Enhanced Details Section */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* Educational Tips Section - Show relevant tips for this category */}
              <Fade in timeout={1200}>
                <Box>
                  <TipDisplay
                    categoryId={classification.category?.id}
                    compact={true}
                    showRefresh={true}
                  />
                </Box>
              </Fade>

              {/* Championship Classification Info */}
              <Fade in timeout={1400}>
                <Paper elevation={12} sx={{
                  p: 5,
                  borderRadius: 5,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,255,248,0.9))',
                  backdropFilter: 'blur(20px)',
                  border: `3px solid ${alpha(getCategoryColor(classification.category?.name), 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '6px',
                    background: getCategoryGradient(classification.category?.name)
                  }
                }}>
                  <Typography variant="h2" fontWeight={900} sx={{
                    mb: 3,
                    background: getCategoryGradient(classification.category?.name),
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    textShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <span style={{ fontSize: '3.5rem' }}>{getCategoryIcon(classification.category?.name)}</span>
                    {classification.category?.name}
                  </Typography>

                  <Box sx={{
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(getCategoryColor(classification.category?.name), 0.1)}, ${alpha(getCategoryColor(classification.category?.name), 0.05)})`,
                    border: `2px solid ${alpha(getCategoryColor(classification.category?.name), 0.2)}`,
                    mb: 4
                  }}>
                    <Typography variant="h5" fontWeight={700} sx={{
                      color: getCategoryColor(classification.category?.name),
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Science sx={{ fontSize: '1.8rem' }} />
                      AI Smart Sorter Recommendation
                    </Typography>
                    <Typography variant="h6" sx={{
                      color: '#2c5530',
                      lineHeight: 1.6,
                      fontWeight: 600
                    }}>
                      {classification.suggestion || 'This item has been classified with championship-level AI precision!'}
                    </Typography>
                  </Box>

                  <Divider sx={{
                    my: 4,
                    background: getCategoryGradient(classification.category?.name),
                    height: '3px',
                    borderRadius: 1
                  }} />

                  {/* Enhanced Confidence Meter */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h5" fontWeight={800} sx={{
                        color: getCategoryColor(classification.category?.name),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <EmojiEvents sx={{ fontSize: '1.8rem', color: '#ffc400' }} />
                        Smart Sorter Confidence
                      </Typography>
                      <Chip
                        label={getConfidenceLabel(classification.resultConfidence)}
                        sx={{
                          background: `linear-gradient(135deg, ${getConfidenceColor(classification.resultConfidence)}, ${alpha(getConfidenceColor(classification.resultConfidence), 0.8)})`,
                          color: 'white',
                          fontWeight: 800,
                          fontSize: '1rem',
                          px: 2
                        }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={classification.resultConfidence}
                      sx={{
                        height: 16,
                        borderRadius: 8,
                        backgroundColor: alpha(getConfidenceColor(classification.resultConfidence), 0.2),
                        border: `2px solid ${alpha(getConfidenceColor(classification.resultConfidence), 0.3)}`,
                        '& .MuiLinearProgress-bar': {
                          background: `linear-gradient(90deg, ${getConfidenceColor(classification.resultConfidence)}, ${alpha(getConfidenceColor(classification.resultConfidence), 0.8)}, ${getConfidenceColor(classification.resultConfidence)})`,
                          borderRadius: 8,
                          animation: 'shimmer 2s ease-in-out infinite',
                        },
                        '@keyframes shimmer': {
                          '0%': { backgroundPosition: '-200% 0' },
                          '100%': { backgroundPosition: '200% 0' },
                        }
                      }}
                    />
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2
                    }}>
                      <Typography variant="h4" fontWeight={900} sx={{
                        color: getConfidenceColor(classification.resultConfidence),
                        textShadow: `0 2px 8px ${alpha(getConfidenceColor(classification.resultConfidence), 0.3)}`
                      }}>
                        {classification.resultConfidence?.toFixed(1)}%
                      </Typography>
                      <Typography variant="body1" color="text.secondary" fontWeight={600}>
                        AI Model Precision
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Fade>

              {/* Enhanced Technical Details */}
              <Fade in timeout={1600}>
                <Paper elevation={12} sx={{
                  p: 4,
                  borderRadius: 5,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(240,248,240,0.9))',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(67, 160, 71, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #43a047, #66bb6a, #43a047)'
                  }
                }}>
                  <Typography variant="h4" fontWeight={800} sx={{
                    mb: 4,
                    color: '#43a047',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Analytics sx={{ fontSize: '2rem', color: '#43a047' }} />
                    Smart Classification Analysis
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.1), rgba(102, 187, 106, 0.05))',
                        border: '2px solid rgba(67, 160, 71, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(67, 160, 71, 0.2)'
                        }
                      }}>
                        <CalendarToday sx={{ fontSize: 50, color: '#43a047', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={600}>
                          Classification Date
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="#1b5e20">
                          {formatDate(classification.classifiedAt)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1), rgba(255, 193, 7, 0.05))',
                        border: '2px solid rgba(255, 152, 0, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(255, 152, 0, 0.2)'
                        }
                      }}>
                        <Psychology sx={{ fontSize: 50, color: '#ff9800', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={600}>
                          AI Model
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="#e65100">
                          {classification.aiModelVersion?.versionName || 'Smart Sorter v2.0'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(66, 165, 245, 0.05))',
                        border: '2px solid rgba(33, 150, 243, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(33, 150, 243, 0.2)'
                        }
                      }}>
                        <Speed sx={{ fontSize: 50, color: '#2196f3', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={600}>
                          Processing
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="#0d47a1">
                          Lightning Fast ⚡
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(171, 71, 188, 0.05))',
                        border: '2px solid rgba(156, 39, 176, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(156, 39, 176, 0.2)'
                        }
                      }}>
                        <TrendingUp sx={{ fontSize: 50, color: '#9c27b0', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={600}>
                          Status
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="#4a148c">
                          {classification.isCorrected ? '🏆 Perfected' : '✨ Original Classification'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>

              {/* Enhanced Feedback Section */}
              {classification.feedbacks && classification.feedbacks.length > 0 && (
                <Fade in timeout={1800}>
                  <Paper elevation={12} sx={{
                    p: 4,
                    borderRadius: 5,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,253,248,0.9))',
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(67, 160, 71, 0.15)'
                  }}>
                    <Typography variant="h4" fontWeight={800} sx={{
                      mb: 4,
                      color: '#43a047',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      💬 Community Feedback on this
                    </Typography>

                    <Stack spacing={3}>
                      {classification.feedbacks.map((feedback, index) => (
                        <Box key={index} sx={{
                          p: 3,
                          borderRadius: 4,
                          background: feedback.correct
                            ? 'linear-gradient(135deg, rgba(67, 160, 71, 0.1), rgba(102, 187, 106, 0.05))'
                            : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(239, 83, 80, 0.05))',
                          border: `3px solid ${feedback.correct ? alpha('#43a047', 0.3) : alpha('#f44336', 0.3)}`,
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(8px)',
                            boxShadow: `0 8px 25px ${feedback.correct ? alpha('#43a047', 0.15) : alpha('#f44336', 0.15)}`
                          },
                          '&::before': {
                            content: `"${feedback.correct ? '✅' : '❌'}"`,
                            position: 'absolute',
                            top: '-10px',
                            right: '15px',
                            fontSize: '1.5rem',
                            background: 'white',
                            borderRadius: '50%',
                            padding: '4px'
                          }
                        }}>
                          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            {feedback.message}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontWeight={600}>
                            {feedback.correct ? '🏆 Confirmed by Community' : '� Needs Review'} •
                            {new Date(feedback.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Fade>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Enhanced Action Buttons */}
        <Fade in timeout={2000}>
          <Paper elevation={20} sx={{
            p: 6,
            mt: 8,
            borderRadius: 6,
            background: `
              linear-gradient(135deg, 
                rgba(255,255,255,0.95) 0%, 
                rgba(248,255,248,0.9) 50%, 
                rgba(240,248,240,0.95) 100%
              )
            `,
            backdropFilter: 'blur(20px)',
            border: '3px solid rgba(67, 160, 71, 0.2)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '8px',
              background: 'linear-gradient(90deg, #43a047, #66bb6a, #ffc400, #ff9800, #43a047)',
              backgroundSize: '200% 100%',
              animation: 'rainbow 3s linear infinite',
            },
            '@keyframes rainbow': {
              '0%': { backgroundPosition: '0% 50%' },
              '100%': { backgroundPosition: '200% 50%' },
            }
          }}>
            <Typography variant="h3" fontWeight={900} sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #43a047, #66bb6a)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              � Classification Complete!
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 5, fontWeight: 600 }}>
              Ready to achieve more sustainability victories?
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
              <Button
                component={Link}
                to="/classify"
                variant="contained"
                size="large"
                startIcon={<PhotoCamera sx={{ fontSize: '1.5rem' }} />}
                sx={{
                  background: getCategoryGradient(classification.category?.name),
                  px: 6,
                  py: 2.5,
                  borderRadius: 4,
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  textTransform: 'none',
                  boxShadow: `0 12px 40px ${alpha(getCategoryColor(classification.category?.name), 0.4)}`,
                  border: '3px solid white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: getCategoryGradient(classification.category?.name),
                    transform: 'translateY(-3px)',
                    boxShadow: `0 16px 50px ${alpha(getCategoryColor(classification.category?.name), 0.6)}`,
                    borderColor: 'white'
                  }
                }}
              >
                Classify More Waste
              </Button>
              <Button
                component={Link}
                to={`/shared/${classification.id}`}
                variant="outlined"
                size="large"
                startIcon={<Public sx={{ fontSize: '1.5rem' }} />}
                sx={{
                  color: getCategoryColor(classification.category?.name),
                  borderColor: getCategoryColor(classification.category?.name),
                  px: 6,
                  py: 2.5,
                  borderRadius: 4,
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  textTransform: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(getCategoryColor(classification.category?.name), 0.1)}, transparent)`,
                    transform: 'translateY(-3px)',
                    boxShadow: `0 16px 50px ${alpha(getCategoryColor(classification.category?.name), 0.4)}`,
                    borderColor: 'white',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${getCategoryColor(classification.category?.name)}, transparent)`,
                      opacity: 0.2,
                      zIndex: 1
                    }
                  }
                }}
              >
                View Public Impact
              </Button>
            </Stack>
          </Paper>
        </Fade>

        {/* Snackbar for share feedback */}
        <Snackbar
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ '& .MuiSnackbarContent-root': { bgcolor: '#43a047', color: 'white' } }}
        />
      </Container>
    </Box>
  );
};

export default ClassificationDetailsPage;
